/**
 * Utility functions per la validazione del codice fiscale italiano
 * 
 * Il codice fiscale italiano ha 16 caratteri con questa struttura:
 * - Posizioni 1-3: prime 3 consonanti del cognome (o X se non bastano)
 * - Posizioni 4-6: trigramma del nome (1ª,3ª,4ª consonante se ≥4 consonanti, altrimenti consonanti+vocali+X)
 * - Posizioni 7-8: Anno di nascita (ultime 2 cifre)
 * - Posizione 9: Mese di nascita (A=Gen, B=Feb, C=Mar, D=Apr, E=Mag, H=Giu, L=Lug, M=Ago, P=Set, R=Ott, S=Nov, T=Dic)
 * - Posizioni 10-11: Giorno di nascita + genere (01-31 maschi, 41-71 femmine)
 * - Posizioni 12-15: Codice catastale del comune
 * - Posizione 16: Carattere di controllo
 */

/**
 * Tabella per il calcolo del carattere di controllo
 * L'algoritmo del codice fiscale italiano usa posizioni 1-based:
 * - Posizioni dispari (1,3,5,7,9,11,13,15): usano valori dispari dalla mappa
 * - Posizioni pari (2,4,6,8,10,12,14): usano valori pari dalla mappa
 * - La somma modulo 26 determina la lettera di controllo
 */
const CHECK_DIGIT_TABLE = {
    // Valori per posizioni dispari
    odd: {
        '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
        'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
        'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
        'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
    },
    // Valori per posizioni pari
    even: {
        '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
        'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
        'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
        'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
    }
};

/**
 * Mappa dei mesi per il codice fiscale
 */
const MONTH_MAP = {
    'A': 1,  // Gennaio
    'B': 2,  // Febbraio
    'C': 3,  // Marzo
    'D': 4,  // Aprile
    'E': 5,  // Maggio
    'H': 6,  // Giugno
    'L': 7,  // Luglio
    'M': 8,  // Agosto
    'P': 9,  // Settembre
    'R': 10, // Ottobre
    'S': 11, // Novembre
    'T': 12  // Dicembre
};

/**
 * Validazione di base: formato e lunghezza
 */
export function validateTaxCodeFormat(taxCode) {
    if (!taxCode) {
        return { 
            valid: false, 
            formatValid: false,
            checksumValid: null,
            error: 'Il codice fiscale è obbligatorio' 
        };
    }
    
    const cleanTaxCode = taxCode.trim().toUpperCase();
    
    if (cleanTaxCode.length !== 16) {
        return { 
            valid: false, 
            formatValid: false,
            checksumValid: null,
            error: `Il codice fiscale deve contenere esattamente 16 caratteri. Lunghezza attuale: ${cleanTaxCode.length}` 
        };
    }
    
    if (!/^[A-Z0-9]{16}$/.test(cleanTaxCode)) {
        const invalidChars = cleanTaxCode.split('').filter(char => !/^[A-Z0-9]$/.test(char));
        return { 
            valid: false, 
            formatValid: false,
            checksumValid: null,
            error: `Il codice fiscale può contenere solo caratteri alfanumerici maiuscoli. Caratteri non validi trovati: ${invalidChars.join(', ')}` 
        };
    }
    
    return { 
        valid: true, 
        formatValid: true,
        checksumValid: null,
        cleanTaxCode 
    };
}

/**
 * Calcola il carattere di controllo del codice fiscale
 */
export function calculateCheckDigit(taxCode15) {
    if (!taxCode15 || taxCode15.length !== 15) {
        return null;
    }
    
    let sum = 0;
    for (let i = 0; i < 15; i++) {
        const char = taxCode15[i];
        const position = i + 1; // Posizioni 1-based
        
        // Posizioni dispari (1,3,5,7,9,11,13,15) usano valori dispari
        // Posizioni pari (2,4,6,8,10,12,14) usano valori pari
        if (position % 2 === 1) {
            // Posizione dispari
            sum += CHECK_DIGIT_TABLE.odd[char] || 0;
        } else {
            // Posizione pari
            sum += CHECK_DIGIT_TABLE.even[char] || 0;
        }
    }
    
    const remainder = sum % 26;
    const checkDigit = String.fromCharCode(65 + remainder); // A=65, Z=90
    
    return checkDigit;
}

/**
 * Valida solo il checksum (carattere di controllo)
 */
export function validateTaxCodeChecksum(taxCode) {
    const formatResult = validateTaxCodeFormat(taxCode);
    if (!formatResult.valid) {
        return { ...formatResult, checksumValid: false };
    }
    
    const cleanTaxCode = formatResult.cleanTaxCode;
    const first15 = cleanTaxCode.substring(0, 15);
    const providedCheckDigit = cleanTaxCode[15];
    
    // Calcola e valida carattere di controllo
    const calculatedCheckDigit = calculateCheckDigit(first15);
    if (!calculatedCheckDigit) {
        return { 
            valid: false, 
            checksumValid: false,
            error: 'Impossibile calcolare il carattere di controllo' 
        };
    }
    
    const checksumValid = calculatedCheckDigit === providedCheckDigit;
    
    if (!checksumValid) {
        return { 
            valid: false, 
            checksumValid: false,
            formatValid: true,
            error: 'Il codice fiscale non è valido' 
        };
    }
    
    return { 
        valid: true, 
        checksumValid: true,
        formatValid: true,
        cleanTaxCode 
    };
}

/**
 * Valida la struttura del codice fiscale (mese, giorno, anno)
 */
export function validateTaxCodeStructure(taxCode) {
    const formatResult = validateTaxCodeFormat(taxCode);
    if (!formatResult.valid) {
        return { ...formatResult, checksumValid: false, formatValid: false };
    }
    
    const cleanTaxCode = formatResult.cleanTaxCode;
    
    // Prima valida il checksum
    const checksumResult = validateTaxCodeChecksum(cleanTaxCode);
    if (!checksumResult.checksumValid) {
        return checksumResult;
    }
    
    // Valida anno (posizioni 7-8, indici 6-7) - dopo cognome (3) + nome (3) = 6
    const yearStr = cleanTaxCode.substring(6, 8);
    // Verifica che siano caratteri numerici
    if (!/^\d{2}$/.test(yearStr)) {
        return { 
            valid: false, 
            formatValid: true,
            checksumValid: true,
            error: `Anno di nascita non valido nel codice fiscale: le posizioni 7-8 devono contenere 2 cifre numeriche, trovato "${yearStr}"` 
        };
    }
    const year = parseInt(yearStr, 10);
    if (isNaN(year) || year < 0 || year > 99) {
        return { 
            valid: false, 
            formatValid: true,
            checksumValid: true,
            error: `Anno di nascita non valido nel codice fiscale: deve essere un numero tra 00 e 99, trovato "${yearStr}"` 
        };
    }
    
    // Valida mese (posizione 9, indice 8)
    const monthChar = cleanTaxCode[8];
    if (!MONTH_MAP[monthChar]) {
        return { 
            valid: false, 
            formatValid: true,
            checksumValid: true,
            error: 'Mese di nascita non valido nel codice fiscale' 
        };
    }
    
    // Valida giorno/genere (posizioni 10-11, indici 9-10)
    const dayStr = cleanTaxCode.substring(9, 11);
    const day = parseInt(dayStr, 10);
    if (isNaN(day) || day < 1 || (day > 31 && day < 41) || day > 71) {
        return { 
            valid: false, 
            formatValid: true,
            checksumValid: true,
            error: 'Giorno di nascita non valido nel codice fiscale' 
        };
    }
    
    return { 
        valid: true, 
        formatValid: true,
        checksumValid: true,
        cleanTaxCode, 
        monthChar, 
        day, 
        year 
    };
}

/**
 * Estrae il mese dal codice fiscale
 */
export function extractMonthFromCF(taxCode) {
    const cleanTaxCode = (taxCode || '').trim().toUpperCase();
    if (cleanTaxCode.length < 9) return null;
    const monthChar = cleanTaxCode[8];
    return MONTH_MAP[monthChar] || null;
}

/**
 * Estrae il giorno dal codice fiscale
 */
export function extractDayFromCF(taxCode) {
    const cleanTaxCode = (taxCode || '').trim().toUpperCase();
    if (cleanTaxCode.length < 11) return null;
    const dayStr = cleanTaxCode.substring(9, 11);
    const day = parseInt(dayStr, 10);
    if (isNaN(day)) return null;
    // Se giorno > 40, è una femmina (41-71), sottraiamo 40
    if (day > 40) {
        return day - 40;
    }
    return day;
}

/**
 * Estrae l'anno dal codice fiscale (solo le ultime 2 cifre)
 */
export function extractYearFromCF(taxCode) {
    const cleanTaxCode = (taxCode || '').trim().toUpperCase();
    if (cleanTaxCode.length < 8) return null;
    const yearStr = cleanTaxCode.substring(6, 8); // Posizioni 7-8 (indici 6-7) - dopo cognome(3) + nome(3) = 6
    const year = parseInt(yearStr, 10);
    if (isNaN(year)) return null;
    
    // Restituisce solo le ultime 2 cifre (00-99)
    return year;
}

/**
 * Estrae l'anno completo (4 cifre) dal codice fiscale, provando entrambi i secoli
 */
export function extractFullYearFromCF(taxCode, birthYear) {
    const year2Digits = extractYearFromCF(taxCode);
    if (year2Digits === null) return null;
    
    // Se abbiamo l'anno di nascita, usalo per determinare il secolo
    if (birthYear) {
        const birthYearLast2 = birthYear % 100;
        if (birthYearLast2 === year2Digits) {
            return birthYear; // L'anno corrisponde già
        }
    }
    
    // Altrimenti prova con il secolo corrente
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    const fullYear1 = currentCentury + year2Digits;
    
    // Se è nel futuro, prova il secolo precedente
    if (fullYear1 > currentYear) {
        return fullYear1 - 100;
    }
    
    return fullYear1;
}

/**
 * Estrae il genere dal codice fiscale
 */
export function extractGenderFromCF(taxCode) {
    const cleanTaxCode = (taxCode || '').trim().toUpperCase();
    if (cleanTaxCode.length < 11) return null;
    const dayStr = cleanTaxCode.substring(9, 11);
    const day = parseInt(dayStr, 10);
    if (isNaN(day)) return null;
    
    if (day >= 1 && day <= 31) {
        return 'MALE';
    } else if (day >= 41 && day <= 71) {
        return 'FEMALE';
    }
    return null;
}

/**
 * Normalizza una stringa per il calcolo del codice fiscale
 * - Converti in maiuscolo
 * - Rimuovi caratteri non alfabetici (spazi, apostrofi, trattini, punti, numeri, simboli)
 * - Normalizza lettere accentate (Unicode NFD + rimozione diacritici)
 */
function normalizeForCF(str) {
    if (!str) return '';
    
    // Converti in maiuscolo
    let normalized = str.toUpperCase();
    
    // Normalizza caratteri accentati (Unicode NFD)
    normalized = normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // Rimuovi diacritici
    
    // Rimuovi tutti i caratteri non alfabetici (spazi, apostrofi, trattini, punti, numeri, simboli)
    normalized = normalized.replace(/[^A-Z]/g, '');
    
    // Unisci nomi multipli (rimuovi spazi rimanenti)
    normalized = normalized.replace(/\s+/g, '');
    
    return normalized;
}

/**
 * Estrae le consonanti da una stringa normalizzata
 * Restituisce l'array delle consonanti in ordine di apparizione
 */
function extractConsonantsArray(normalizedStr) {
    if (!normalizedStr) return [];
    return normalizedStr.split('').filter(char => !'AEIOU'.includes(char));
}

/**
 * Estrae le vocali da una stringa normalizzata
 * Restituisce l'array delle vocali in ordine di apparizione
 */
function extractVowelsArray(normalizedStr) {
    if (!normalizedStr) return [];
    return normalizedStr.split('').filter(char => 'AEIOU'.includes(char));
}

/**
 * Calcola il trigramma del nome secondo le regole del codice fiscale italiano
 * Regole:
 * - Se il nome ha 4+ consonanti: prendi 1ª, 3ª, 4ª consonante (salta la 2ª)
 * - Se <4 consonanti: prendi consonanti in ordine, poi vocali, poi X se necessario
 */
function calculateNameTrigram(name) {
    if (!name) return 'XXX';
    
    // 1. Normalizzazione preliminare
    const normalized = normalizeForCF(name);
    if (!normalized || normalized.length === 0) return 'XXX';
    
    // 2. Classificazione delle lettere
    const consonants = extractConsonantsArray(normalized);
    const vowels = extractVowelsArray(normalized);
    
    // 3. Regola speciale del nome: se ha 4+ consonanti, prendi 1ª, 3ª, 4ª
    if (consonants.length >= 4) {
        const trigram = consonants[0] + consonants[2] + consonants[3]; // 1ª, 3ª, 4ª
        return trigram;
    }
    
    // 4. Caso con <4 consonanti: prendi consonanti + vocali fino a 3, completa con X
    let result = '';
    
    // Aggiungi consonanti in ordine
    for (let i = 0; i < consonants.length && result.length < 3; i++) {
        result += consonants[i];
    }
    
    // Se non bastano, aggiungi vocali in ordine
    for (let i = 0; i < vowels.length && result.length < 3; i++) {
        result += vowels[i];
    }
    
    // Se ancora non bastano, completa con X
    while (result.length < 3) {
        result += 'X';
    }
    
    // Prendi esattamente 3 caratteri
    return result.substring(0, 3);
}

/**
 * Estrae il cognome dal codice fiscale (prime 3 posizioni)
 */
export function extractSurnameFromCF(taxCode) {
    const cleanTaxCode = (taxCode || '').trim().toUpperCase();
    if (cleanTaxCode.length < 3) return null;
    return cleanTaxCode.substring(0, 3);
}

/**
 * Estrae il trigramma del nome dal codice fiscale (posizioni 4-6)
 */
export function extractNameTrigramFromCF(taxCode) {
    const cleanTaxCode = (taxCode || '').trim().toUpperCase();
    if (cleanTaxCode.length < 6) return null;
    return cleanTaxCode.substring(3, 6);
}

/**
 * Calcola il trigramma del cognome secondo le regole del codice fiscale italiano
 * Regole:
 * - Prendi le prime 3 consonanti del cognome
 * - Se non bastano, aggiungi vocali
 * - Se ancora non bastano, completa con X
 */
function calculateSurnameTrigram(surname) {
    if (!surname) return 'XXX';
    
    const normalized = normalizeForCF(surname);
    if (!normalized || normalized.length === 0) return 'XXX';
    
    const consonants = extractConsonantsArray(normalized);
    const vowels = extractVowelsArray(normalized);
    
    let result = '';
    
    // Aggiungi consonanti in ordine (fino a 3)
    for (let i = 0; i < consonants.length && result.length < 3; i++) {
        result += consonants[i];
    }
    
    // Se non bastano, aggiungi vocali in ordine
    for (let i = 0; i < vowels.length && result.length < 3; i++) {
        result += vowels[i];
    }
    
    // Se ancora non bastano, completa con X
    while (result.length < 3) {
        result += 'X';
    }
    
    return result.substring(0, 3);
}

/**
 * Verifica se il cognome corrisponde al codice fiscale
 * Il CF contiene il trigramma del cognome (prime 3 posizioni)
 * Gestisce cognomi multipli e composti
 */
function validateSurnameWithCF(surname, taxCode) {
    if (!surname || !taxCode) return { valid: true }; // Se mancano dati, non validare
    
    const cfSurnameTrigram = extractSurnameFromCF(taxCode);
    if (!cfSurnameTrigram) return { valid: true };
    
    // Calcola il trigramma del cognome secondo le regole CF
    const calculatedSurnameTrigram = calculateSurnameTrigram(surname);
    
    // Debug
    console.log('Validazione Cognome - CF:', {
        cfSurnameTrigram
    }, 'Surname:', {
        surname,
        calculatedSurnameTrigram
    });
    
    // Confronta i trigrammi
    if (calculatedSurnameTrigram !== cfSurnameTrigram) {
        return {
            valid: false,
            error: `Il cognome nel codice fiscale non corrisponde al cognome inserito`
        };
    }
    
    return { valid: true };
}

/**
 * Verifica se il nome corrisponde al codice fiscale
 * Il CF contiene il trigramma del nome (posizioni 4-6)
 * Gestisce nomi multipli e composti con la regola speciale (1ª, 3ª, 4ª consonante se ≥4 consonanti)
 */
function validateNameWithCF(firstName, taxCode) {
    if (!firstName || !taxCode) return { valid: true }; // Se mancano dati, non validare
    
    const cfNameTrigram = extractNameTrigramFromCF(taxCode);
    if (!cfNameTrigram) return { valid: true };
    
    // Calcola il trigramma del nome secondo le regole CF (con la regola speciale)
    const calculatedNameTrigram = calculateNameTrigram(firstName);
    
    // Debug
    console.log('Validazione Nome - CF:', {
        cfNameTrigram
    }, 'FirstName:', {
        firstName,
        calculatedNameTrigram
    });
    
    // Confronta i trigrammi
    if (calculatedNameTrigram !== cfNameTrigram) {
        return {
            valid: false,
            error: `Il nome nel codice fiscale non corrisponde al nome inserito`
        };
    }
    
    return { valid: true };
}

/**
 * Valida la coerenza del codice fiscale con i dati anagrafici
 */
export function validateTaxCodeConsistency(taxCode, userData) {
    const structureResult = validateTaxCodeStructure(taxCode);
    if (!structureResult.valid) {
        return structureResult;
    }
    
    const errors = [];
    
    // Verifica cognome se disponibile
    if (userData.lastName) {
        const surnameCheck = validateSurnameWithCF(userData.lastName, taxCode);
        if (!surnameCheck.valid) {
            errors.push(surnameCheck.error);
        }
    }
    
    // Verifica nome se disponibile
    if (userData.firstName) {
        const nameCheck = validateNameWithCF(userData.firstName, taxCode);
        if (!nameCheck.valid) {
            errors.push(nameCheck.error);
        }
    }
    
    // Verifica data di nascita se disponibile
    if (userData.dateOfBirth) {
        // Gestisci diversi formati di data (Date object, stringa ISO, ecc.)
        let birthDate;
        
        // Debug: log del valore originale
        console.log('Validazione CF - dateOfBirth originale:', userData.dateOfBirth, 'tipo:', typeof userData.dateOfBirth);
        
        if (userData.dateOfBirth instanceof Date) {
            birthDate = userData.dateOfBirth;
        } else if (typeof userData.dateOfBirth === 'string') {
            // Se è una stringa, prova a parsare come ISO string o altri formati comuni
            // Rimuovi eventuali spazi e caratteri extra
            const cleanDateStr = userData.dateOfBirth.trim();
            birthDate = new Date(cleanDateStr);
        } else if (typeof userData.dateOfBirth === 'number') {
            // Se è un timestamp numerico
            birthDate = new Date(userData.dateOfBirth);
        } else if (userData.dateOfBirth && typeof userData.dateOfBirth === 'object') {
            // Se è un oggetto con proprietà di data (formato del datepicker)
            // Alcuni datepicker restituiscono oggetti con year, month, day
            if (userData.dateOfBirth.year && userData.dateOfBirth.month !== undefined && userData.dateOfBirth.day) {
                // I mesi nei datepicker sono spesso 1-based, ma Date usa 0-based
                birthDate = new Date(userData.dateOfBirth.year, userData.dateOfBirth.month - 1, userData.dateOfBirth.day);
            } else {
                // Prova comunque a convertire
                birthDate = new Date(userData.dateOfBirth);
            }
        } else {
            // Ultimo tentativo
            birthDate = new Date(userData.dateOfBirth);
        }
        
        // Debug: log della data parsata
        console.log('Validazione CF - birthDate parsata:', birthDate, 'getTime():', birthDate.getTime(), 'isNaN:', isNaN(birthDate.getTime()));
        
        if (!isNaN(birthDate.getTime())) {
            const cfMonth = extractMonthFromCF(taxCode);
            const cfDay = extractDayFromCF(taxCode);
            const cfYear = extractYearFromCF(taxCode);
            
            const birthMonth = birthDate.getMonth() + 1; // JavaScript months are 0-indexed
            const birthDay = birthDate.getDate();
            const birthYear = birthDate.getFullYear();
            
            // Debug: log dei valori estratti
            console.log('Validazione CF - CF:', {
                month: cfMonth,
                day: cfDay,
                year: cfYear
            }, 'Birth:', {
                month: birthMonth,
                day: birthDay,
                year: birthYear
            });
            
            // Valida che l'anno sia ragionevole (1900-2100)
            if (birthYear < 1900 || birthYear > 2100) {
                // Se l'anno non è valido, salta la validazione dell'anno
                console.warn('Validazione CF - Anno di nascita non ragionevole:', birthYear);
            } else {
                if (cfMonth && cfMonth !== birthMonth) {
                    errors.push(`Il mese di nascita nel codice fiscale (${cfMonth}) non corrisponde alla data di nascita (${birthMonth})`);
                }
                
                if (cfDay && cfDay !== birthDay) {
                    errors.push(`Il giorno di nascita nel codice fiscale (${cfDay}) non corrisponde alla data di nascita (${birthDay})`);
                }
                
                if (cfYear !== null) {
                    // Confronta solo le ultime 2 cifre dell'anno (il CF contiene solo le ultime 2 cifre)
                    const cfYearLast2 = cfYear; // cfYear è già le ultime 2 cifre (00-99)
                    const birthYearLast2 = birthYear % 100;
                    
                    console.log('Validazione CF - Confronto anno:', {
                        cfYearLast2,
                        birthYearLast2,
                        match: cfYearLast2 === birthYearLast2
                    });
                    
                    if (cfYearLast2 !== birthYearLast2) {
                        // Formatta l'anno con padding se necessario per il messaggio
                        const cfYearFormatted = cfYearLast2.toString().padStart(2, '0');
                        const birthYearFormatted = birthYearLast2.toString().padStart(2, '0');
                        errors.push(`L'anno di nascita nel codice fiscale (${cfYearFormatted}) non corrisponde alla data di nascita (${birthYearFormatted})`);
                    }
                }
            }
        } else {
            console.error('Validazione CF - Impossibile parsare la data di nascita:', userData.dateOfBirth);
        }
    }
    
    // Verifica genere se disponibile
    if (userData.gender) {
        const cfGender = extractGenderFromCF(taxCode);
        const userGender = userData.gender.toUpperCase();
        
        if (cfGender) {
            const genderMap = {
                'MALE': 'MALE',
                'FEMALE': 'FEMALE',
                'M': 'MALE',
                'F': 'FEMALE'
            };
            
            const normalizedUserGender = genderMap[userGender] || userGender;
            
            if (cfGender !== normalizedUserGender) {
                errors.push(`Il genere nel codice fiscale (${cfGender === 'MALE' ? 'Maschile' : 'Femminile'}) non corrisponde al genere selezionato (${normalizedUserGender === 'MALE' ? 'Maschile' : 'Femminile'})`);
            }
        }
    }
    
    if (errors.length > 0) {
        return { 
            valid: false, 
            formatValid: structureResult.formatValid,
            checksumValid: structureResult.checksumValid,
            error: errors.join('. ') 
        };
    }
    
    return { 
        valid: true, 
        formatValid: structureResult.formatValid,
        checksumValid: structureResult.checksumValid,
        cleanTaxCode: structureResult.cleanTaxCode 
    };
}

/**
 * Funzione principale di validazione completa
 */
export function validateItalianTaxCode(taxCode, userData = {}) {
    // Prima validazione di formato
    const formatResult = validateTaxCodeFormat(taxCode);
    if (!formatResult.valid) {
        return {
            ...formatResult,
            message: 'Formato non valido: ' + formatResult.error
        };
    }
    
    // Validazione struttura (incluso checksum)
    const structureResult = validateTaxCodeStructure(formatResult.cleanTaxCode);
    if (!structureResult.valid) {
        // Costruisci messaggio più dettagliato
        let errorMessage = '';
        if (!structureResult.formatValid) {
            errorMessage = 'Formato non valido: ' + structureResult.error;
        } else if (!structureResult.checksumValid) {
            // Per il checksum, mostra solo un messaggio generico senza dettagli
            errorMessage = 'Il codice fiscale non è valido';
        } else {
            errorMessage = structureResult.error;
        }
        
        return {
            ...structureResult,
            message: errorMessage
        };
    }
    
    // Validazione coerenza con dati anagrafici
    const consistencyResult = validateTaxCodeConsistency(formatResult.cleanTaxCode, userData);
    if (!consistencyResult.valid) {
        return {
            ...consistencyResult,
            message: consistencyResult.error
        };
    }
    
    // Tutto ok!
    return { 
        valid: true, 
        formatValid: true,
        checksumValid: true,
        cleanTaxCode: formatResult.cleanTaxCode,
        message: 'Codice fiscale valido'
    };
}

